import { useCallback } from 'react';
import { ChevronLeft, ChevronRight, DollarSign } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { usePayroll } from '../../hooks/usePayroll';
import { EmptyState } from '../common/EmptyState';
import { ExportMenu } from '../common/ExportMenu';
import { exportPayrollCSV } from '../../utils/exportPayrollCSV';
import { formatMoney } from '../../lib/money';

interface HoursPayrollProps {
  businessId: string;
  otThreshold: number;
  otWarningThreshold: number;
}

const TH: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'right',
  borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  fontSize: 13, color: palette.g700, padding: '10px 12px', textAlign: 'right',
  borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap',
};

function formatWeekRange(start: Date, end: Date): string {
  const sameMonth = start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${start.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} – ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${start.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}, ${end.getFullYear()}`;
}

export function HoursPayroll({ businessId, otThreshold, otWarningThreshold }: HoursPayrollProps) {
  const { payPeriodData, totals, weekStart, weekEnd, setWeek, loading, error } = usePayroll(businessId, otThreshold);

  const navWeek = useCallback((dir: -1 | 1) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeek(d);
  }, [weekStart, setWeek]);

  const hasOTIssues = payPeriodData.some(r => r.overtimeHours > 0);
  const hasOTWarnings = payPeriodData.some(r => r.actualHours >= otWarningThreshold && r.overtimeHours === 0);

  if (loading) {
    return <div style={{ padding: '12px 0' }}>{[1, 2, 3, 4].map(i => <div key={i} style={{ height: 14, background: palette.g100, borderRadius: 4, marginBottom: 12, width: `${60 + i * 8}%`, animation: 'crewbit-shimmer 1.5s ease infinite', backgroundSize: '800px 100%', backgroundImage: `linear-gradient(90deg, ${palette.g100} 25%, ${palette.g150} 50%, ${palette.g100} 75%)` }} />)}</div>;
  }
  if (error) {
    return <div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div>;
  }

  return (
    <div>
      {/* Week nav + export */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => navWeek(-1)} style={{
            width: 30, height: 30, borderRadius: 6, border: `1px solid ${palette.g200}`,
            background: palette.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><ChevronLeft size={15} color={palette.g600} /></button>
          <button onClick={() => navWeek(1)} style={{
            width: 30, height: 30, borderRadius: 6, border: `1px solid ${palette.g200}`,
            background: palette.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><ChevronRight size={15} color={palette.g600} /></button>
          <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800, marginLeft: 4 }}>
            {formatWeekRange(weekStart, weekEnd)}
          </span>
        </div>
        <ExportMenu options={[
          { label: 'Payroll Summary (CSV)', description: 'Accounting-ready with deduction estimates', onClick: () => exportPayrollCSV(payPeriodData, totals, weekStart, weekEnd) },
        ]} />
      </div>

      {/* OT warning banner */}
      {hasOTIssues && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 12,
          background: palette.redWash, borderLeft: `3px solid ${palette.red}`,
          fontSize: 13, color: palette.red, fontWeight: 500,
        }}>
          One or more team members have exceeded the {otThreshold}h overtime threshold this week.
        </div>
      )}
      {!hasOTIssues && hasOTWarnings && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 12,
          background: palette.amberWash, borderLeft: `3px solid ${palette.amber}`,
          fontSize: 13, color: palette.amber, fontWeight: 500,
        }}>
          Some team members are approaching the overtime threshold ({otWarningThreshold}h+).
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...TH, textAlign: 'left' }}>Name</th>
              <th style={TH}>Type</th>
              <th style={TH}>Scheduled</th>
              <th style={TH}>Actual</th>
              <th style={TH}>Regular</th>
              <th style={TH}>Overtime</th>
              <th style={TH}>Rate</th>
              <th style={TH}>Gross Pay</th>
              <th style={{ ...TH, width: 40 }}>ESA</th>
            </tr>
          </thead>
          <tbody>
            {payPeriodData.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 0 }}><EmptyState icon={<DollarSign size={22} />} title="No hours recorded" subtitle="Hours will appear here once your team clocks in." /></td></tr>
            ) : payPeriodData.map(r => {
              const isOver = r.overtimeHours > 0;
              const isWarn = r.otWarning && !isOver;
              return (
                <tr key={r.userId}
                  style={{ transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ ...TD, textAlign: 'left', fontWeight: 500, color: palette.g800 }}>{r.name}</td>
                  <td style={TD}>
                    <span style={{
                      fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4, textTransform: 'capitalize',
                      background: r.employmentType === 'contractor' ? palette.amberWash : palette.greenWash,
                      color: r.employmentType === 'contractor' ? palette.amber : palette.green,
                    }}>{r.employmentType}</span>
                  </td>
                  <td style={TD}>{r.scheduledHours.toFixed(1)}h</td>
                  <td style={TD}>{r.actualHours.toFixed(1)}h</td>
                  <td style={TD}>{r.regularHours.toFixed(1)}h</td>
                  <td style={{ ...TD, color: isOver ? palette.red : palette.g700, fontWeight: isOver ? 600 : 400 }}>
                    {r.overtimeHours > 0 ? `${r.overtimeHours.toFixed(1)}h` : '—'}
                  </td>
                  <td style={TD}>{formatMoney(r.hourlyRate)}</td>
                  <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{formatMoney(r.grossPay)}</td>
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: 5, margin: '0 auto',
                      background: isOver ? palette.red : isWarn ? palette.amber : palette.green,
                    }} />
                  </td>
                </tr>
              );
            })}
            {/* Totals row */}
            {payPeriodData.length > 0 && (
              <tr style={{ background: palette.g50 }}>
                <td style={{ ...TD, textAlign: 'left', fontWeight: 700, color: palette.g800, borderTop: `2px solid ${palette.g200}` }}>Total</td>
                <td style={{ ...TD, borderTop: `2px solid ${palette.g200}` }} />
                <td style={{ ...TD, fontWeight: 600, borderTop: `2px solid ${palette.g200}` }}>{totals.scheduledHours.toFixed(1)}h</td>
                <td style={{ ...TD, fontWeight: 600, borderTop: `2px solid ${palette.g200}` }}>{totals.actualHours.toFixed(1)}h</td>
                <td style={{ ...TD, fontWeight: 600, borderTop: `2px solid ${palette.g200}` }}>{totals.regularHours.toFixed(1)}h</td>
                <td style={{ ...TD, fontWeight: 600, color: totals.overtimeHours > 0 ? palette.red : palette.g700, borderTop: `2px solid ${palette.g200}` }}>
                  {totals.overtimeHours > 0 ? `${totals.overtimeHours.toFixed(1)}h` : '—'}
                </td>
                <td style={{ ...TD, borderTop: `2px solid ${palette.g200}` }} />
                <td style={{ ...TD, fontWeight: 700, color: palette.g800, borderTop: `2px solid ${palette.g200}` }}>{formatMoney(totals.grossPay)}</td>
                <td style={{ ...TD, borderTop: `2px solid ${palette.g200}` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
